import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Avatar,
  Chip,
  CircularProgress,
  Stack,
  Grid,
  Button,
  Divider,
  Card,
  CardContent,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Tooltip,
  InputAdornment,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';

import {
  Search,
  LocalHospital,
  Science,
  CameraAlt,
  Medication,
  MonitorHeart,
  History,
  Phone,
  LocationOn,
  CalendarToday,
  Badge,
  Shield,
  Notes,
  HotelRounded,
  FilterList,
  Assignment,
  EventNote,
} from "@mui/icons-material";

import axios from "axios";
import Swal from "sweetalert2";

// ── Import existing history dialogs ──
import MedicalHistoryDialog from "../Doctors/MedicalHistoryDialog";
import LabHistoryDialog from "../Doctors/LabHistoryDialog";
import ScanHistoryDialog from "../Doctors/ScanHistoryDialog";
import MedicationHistory from "../Pharmacy/MedicationHistory";
import VitalsHistoryDialog from "../Doctors/VitalsHistoryDialog";

// ── Import new VisitRecordDialog ──
import VisitRecordDialog from "./VisitRecordDialog";

// ── BaseURL endpoints — add these to BaseURL.js ──
// export const searchAllPatientsdb       = BaseURL + '/api/patients/search';
// export const getPatientVisitSummarydb  = BaseURL + '/api/patients/visit-summary';
//   Query: SELECT dat, d_dat, member_no, documenting, procedure_, which
//          FROM claim_information WHERE id = :patientId
//          [AND dat >= :from] [AND dat <= :to] ORDER BY dat DESC
import {
  searchAllPatientsdb,
  getPatientVisitSummarydb,
} from "../BaseURL";


// ─────────────────────────────────────────────────────────────
// VISIT HISTORY TABLE — sourced from claim_information
// ─────────────────────────────────────────────────────────────
const VisitHistoryTable = ({ patientData, onOpenVisit }) => {
  const muiTheme = useTheme();
  const isDark   = muiTheme.palette.mode === 'dark';
  const PAPER_BG = muiTheme.palette.background.paper;
  const SOFT_BG  = isDark ? '#0E0E14' : '#FAFCFC';
  const HEAD_BG  = isDark ? '#101018' : '#F8FAFB';
  const TITLE_COL  = muiTheme.palette.text.primary;
  const MUTED_COL  = muiTheme.palette.text.secondary;
  const BORDER_COL = muiTheme.palette.divider;

  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const loadVisits = async (from = fromDate, to = toDate) => {
    if (!patientData) return;
    setLoading(true);
    try {
      const response = await axios.get(getPatientVisitSummarydb, {
        params: { patientId: patientData.id, from: from || undefined, to: to || undefined },
      });
      if (response.data.success) {
        setVisits(response.data.visits || []);
        setPage(0);
      }
    } catch (err) {
      console.error("Error loading visit history:", err);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (patientData) loadVisits(); }, [patientData]);

  const handleClearFilter = () => {
    setFromDate("");
    setToDate("");
    loadVisits("", "");
  };

  // dat !== d_dat  →  IPD;  otherwise OPD
  const getType = (row) =>
    row.d_dat && row.d_dat !== row.dat ? "IPD" : "OPD";

  const getDays = (dat, dDat) => {
    if (!dDat || dat === dDat) return null;
    const diff = Math.round((new Date(dDat) - new Date(dat)) / 86400000);
    return diff > 0 ? diff : null;
  };

  const paginated = visits.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        mb: 3,
        border: `1px solid ${BORDER_COL}`,
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {/* ── Header row ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: SOFT_BG,
          borderBottom: `1px solid ${BORDER_COL}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#F0FDFA", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EventNote sx={{ color: "#0D9488", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: TITLE_COL }}>
              Visit History
            </Typography>
            <Typography variant="caption" sx={{ color: MUTED_COL }}>
              {loading ? "Loading…" : `${visits.length} visit${visits.length !== 1 ? "s" : ""} found`}
            </Typography>
          </Box>
        </Box>

        {/* Date filter */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <FilterList sx={{ color: MUTED_COL, fontSize: 18 }} />
          <Typography variant="caption" fontWeight={600} sx={{ color: MUTED_COL }}>Period:</Typography>
          <TextField
            type="date" label="From" size="small" value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 148, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem", bgcolor: PAPER_BG, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0D9488" } } }}
          />
          <Typography variant="caption" sx={{ color: MUTED_COL }}>–</Typography>
          <TextField
            type="date" label="To" size="small" value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 148, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.82rem", bgcolor: PAPER_BG, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0D9488" } } }}
          />
          <Button
            variant="contained" size="small" onClick={() => loadVisits()}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg,#0F766E,#0D9488)", boxShadow: "none", px: 2 }}
          >
            Apply
          </Button>
          {(fromDate || toDate) && (
            <Button variant="outlined" size="small" onClick={handleClearFilter}
              sx={{ borderRadius: 2, textTransform: "none", borderColor: BORDER_COL, color: MUTED_COL }}>
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Table ── */}
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {[
                { h: "#",                  w: "3%" },
                { h: "Visit Date",         w: "11%" },
                { h: "Discharge",          w: "11%" },
                { h: "Type / Days",        w: "11%" },
                { h: "Membership No.",     w: "12%" },
                { h: "Service",            w: "12%" },
                { h: "Diagnosis / Procedure", w: "31%" },
                { h: "Action",             w: "9%", center: true },
              ].map((col) => (
                <TableCell key={col.h} align={col.center ? "center" : "left"}
                  sx={{ fontWeight: 700, fontSize: "0.75rem", color: MUTED_COL, bgcolor: HEAD_BG,
                    borderBottom: `2px solid ${BORDER_COL}`, width: col.w, py: 1.5, whiteSpace: "nowrap" }}>
                  {col.h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} sx={{ color: "#0D9488" }} />
                  <Typography variant="body2" sx={{ color: MUTED_COL }} sx={{ mt: 1 }}>Loading…</Typography>
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <EventNote sx={{ fontSize: 36, color: MUTED_COL, mb: 1 }} />
                  <Typography variant="body2" sx={{ color: MUTED_COL }}>
                    No visits found{(fromDate || toDate) ? " for the selected period" : ""}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((row, idx) => {
                const type = getType(row);
                const isIPD = type === "IPD";
                const days = getDays(row.dat, row.d_dat);
                const diagProc = [row.documenting, row.procedure_].filter(Boolean).join(" / ");

                return (
                  <TableRow key={idx} hover
                    sx={{
                      "&:hover": { bgcolor: isIPD ? "#F5F3FF44" : "#F0FDFA44" },
                      borderLeft: `3px solid ${isIPD ? "#7C3AED" : "#0D9488"}`,
                    }}
                  >
                    <TableCell sx={{ py: 1.5, fontSize: "0.75rem", color: MUTED_COL, fontWeight: 600 }}>
                      {page * rowsPerPage + idx + 1}
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontWeight={700} fontSize="0.82rem" sx={{ color: TITLE_COL }}>
                        {row.dat || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontSize="0.82rem" color={row.d_dat ? "#334155" : "#CBD5E1"}>
                        {row.d_dat || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                        <Chip
                          label={type}
                          size="small"
                          icon={isIPD
                            ? <HotelRounded sx={{ fontSize: "13px !important" }} />
                            : <LocalHospital sx={{ fontSize: "13px !important" }} />
                          }
                          sx={{
                            fontWeight: 700, fontSize: "0.68rem", height: 20,
                            bgcolor: isIPD ? "#F5F3FF" : "#F0FDFA",
                            color: isIPD ? "#6D28D9" : "#0D9488",
                            "& .MuiChip-icon": { color: isIPD ? "#6D28D9" : "#0D9488" },
                          }}
                        />
                        {isIPD && days && (
                          <Typography variant="caption" sx={{ color: MUTED_COL }} fontSize="0.68rem">
                            {days} day{days !== 1 ? "s" : ""}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      {row.member_no
                        ? <Chip label={row.member_no} size="small" sx={{ fontSize: "0.72rem", bgcolor: "#FFF7ED", color: "#C2410C", fontWeight: 600 }} />
                        : <Typography variant="caption" sx={{ color: MUTED_COL }}>—</Typography>
                      }
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Typography variant="body2" fontSize="0.82rem" color="#334155">
                        {row.which || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, maxWidth: 0 }}>
                      <Tooltip title={diagProc || ""} arrow placement="top">
                        <Typography variant="body2" fontSize="0.82rem" color="#475569"
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}>
                          {diagProc || <span style={{ color: MUTED_COL }}>—</span>}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      <Tooltip title="Open Medical Record" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onOpenVisit({ ...row, isIPD, dayCount: days })}
                          sx={{
                            width: 32, height: 32,
                            bgcolor: isIPD ? "#F5F3FF" : "#F0FDFA",
                            color: isIPD ? "#6D28D9" : "#0D9488",
                            "&:hover": {
                              bgcolor: isIPD ? "#EDE9FE" : "#CCFBF1",
                              transform: "scale(1.1)", transition: "all 0.15s",
                            },
                          }}
                        >
                          <Assignment sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {visits.length > 0 && (
        <Box sx={{ borderTop: "1px solid #E2E8F0" }}>
          <TablePagination
            component="div"
            count={visits.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 8, 15, 25]}
            sx={{ "& .MuiTablePagination-toolbar": { minHeight: 44 }, "& *": { fontSize: "0.8rem" } }}
          />
        </Box>
      )}
    </Paper>
  );
};


// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────
const MedicalHistoryPage = () => {
  const muiTheme = useTheme();
  const isDark   = muiTheme.palette.mode === 'dark';
  const PAPER_BG = muiTheme.palette.background.paper;
  const SOFT_BG  = isDark ? '#0E0E14' : '#F8FAFB';
  const TITLE_COL  = muiTheme.palette.text.primary;
  const MUTED_COL  = muiTheme.palette.text.secondary;
  const BORDER_COL = muiTheme.palette.divider;

  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Legacy dialogs
  const [medHistoryOpen, setMedHistoryOpen] = useState(false);
  const [labHistoryOpen, setLabHistoryOpen] = useState(false);
  const [scanHistoryOpen, setScanHistoryOpen] = useState(false);
  const [medHisOpen, setMedHisOpen] = useState(false);
  const [vitalsHistoryOpen, setVitalsHistoryOpen] = useState(false);

  // Visit record dialog
  const [visitRecordOpen, setVisitRecordOpen] = useState(false);
  const [selectedVisitRow, setSelectedVisitRow] = useState(null);

  const searchRef = useRef(null);
  useEffect(() => { if (searchRef.current) searchRef.current.focus(); }, []);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      Swal.fire({ icon: "info", title: "Enter Search Term", text: "Please enter a patient ID to search.", timer: 2000, showConfirmButton: false });
      return;
    }
    setSearchLoading(true);
    setHasSearched(true);
    setSelectedPatient(null);
    try {
      const response = await axios.get(searchAllPatientsdb, { params: { search: searchTerm.trim() } });
      if (response.data.success && response.data.results?.length > 0) {
        setSelectedPatient(response.data.results[0]);
      } else {
        Swal.fire({ icon: "warning", title: "Not Found", text: `No patient found with ID "${searchTerm.trim()}"`, timer: 2500, showConfirmButton: false });
      }
    } catch {
      Swal.fire({ icon: "error", title: "Search Failed", text: "Unable to search patients. Please try again." });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleKeyPress = (e) => { if (e.key === "Enter") handleSearch(); };
  const handleClearSelection = () => { setSelectedPatient(null); setHasSearched(false); };
  const handleOpenVisit = (row) => { setSelectedVisitRow(row); setVisitRecordOpen(true); };

  const historyCards = [
    { key: "medical",   title: "Medical Records",   subtitle: "Complaints, exams, diagnoses & procedures",   icon: <Notes sx={{ fontSize: 32 }} />,       gradient: "linear-gradient(135deg,#0D9488,#14B8A6)", shadow: "rgba(13,148,136,0.35)",   onClick: () => setMedHistoryOpen(true) },
    { key: "vitals",    title: "Vital Signs",        subtitle: "Temperature, BP, pulse, BMI & more",          icon: <MonitorHeart sx={{ fontSize: 32 }} />, gradient: "linear-gradient(135deg,#DC2626,#EF4444)", shadow: "rgba(220,38,38,0.35)",    onClick: () => setVitalsHistoryOpen(true) },
    { key: "lab",       title: "Laboratory",         subtitle: "Lab tests, results & flags",                  icon: <Science sx={{ fontSize: 32 }} />,      gradient: "linear-gradient(135deg,#7C3AED,#8B5CF6)", shadow: "rgba(124,58,237,0.35)",   onClick: () => setLabHistoryOpen(true) },
    { key: "scan",      title: "Scan / Imaging",     subtitle: "Ultrasound, X-ray, CT & MRI results",         icon: <CameraAlt sx={{ fontSize: 32 }} />,    gradient: "linear-gradient(135deg,#2563EB,#3B82F6)", shadow: "rgba(37,99,235,0.35)",    onClick: () => setScanHistoryOpen(true) },
    { key: "medication",title: "Medication",         subtitle: "Prescriptions, dosage & dispensing history",  icon: <Medication sx={{ fontSize: 32 }} />,   gradient: "linear-gradient(135deg,#D97706,#F59E0B)", shadow: "rgba(217,119,6,0.35)",    onClick: () => setMedHisOpen(true) },
  ];

  return (
    <Box sx={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>

      {/* ── Page Header ── */}
      <Box sx={{ mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5,
            bgcolor: "#F0FDFA",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <History sx={{ fontSize: 20, color: "#0D9488" }} />
          </Box>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px" }}>
            Medical History
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "0.88rem", color: "#64748b", ml: 6 }}>
          Search for any patient and view their complete medical records
        </Typography>
      </Box>

      <Box>

        {/* ── Search ── */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, mb: 2.5, border: "1px solid #e5e9ef", bgcolor: "#fff" }}>
          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: "#94a3b8", mb: 1.5, letterSpacing: "0.6px", textTransform: "uppercase" }}>Find Patient</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              inputRef={searchRef} fullWidth placeholder="Enter Patient ID or Name…"
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyPress={handleKeyPress}
              size="medium"
              sx={{ flex: 1, minWidth: 280, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafb",
                "&.Mui-focused": { bgcolor: PAPER_BG, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#0D9488", borderWidth: 1.5 } } } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: MUTED_COL }} /></InputAdornment> }}
            />
            <Button variant="contained" onClick={handleSearch} disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={18} color="inherit" /> : <Search />}
              sx={{ px: 3, py: 1.25, borderRadius: 2, bgcolor: "#0D9488", fontWeight: 600, textTransform: "none", fontSize: "0.9rem", boxShadow: "none",
                "&:hover": { bgcolor: "#0B7A70", boxShadow: "0 4px 12px rgba(13,148,136,0.25)" } }}>
              Search
            </Button>
          </Box>
        </Paper>

        {selectedPatient && (
          <>
            {/* ── Patient Card ── */}
            <Paper elevation={0} sx={{ borderRadius: 3, mb: 3, border: `1px solid ${BORDER_COL}`, boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden" }}>
              <Box sx={{ height: 4, background: "linear-gradient(90deg,#0F766E,#14B8A6,#2DD4BF)" }} />
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, flexWrap: "wrap", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Avatar sx={{ width: 72, height: 72, bgcolor: "#0D9488", fontSize: "1.6rem", fontWeight: 700, boxShadow: "0 4px 16px rgba(13,148,136,0.3)" }}>
                      {selectedPatient.fullName?.split(" ").map((n) => n[0]).join("").slice(0, 2) || "P"}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={700} sx={{ color: TITLE_COL, mb: 0.5 }}>{selectedPatient.fullName}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip icon={<Badge sx={{ fontSize: 16 }} />} label={`ID: ${selectedPatient.id}`} size="small" sx={{ fontWeight: 600, bgcolor: "#F0FDFA", color: "#0D9488" }} />
                        <Chip label={selectedPatient.gender} size="small"
                          sx={{ fontWeight: 600, bgcolor: selectedPatient.gender === "MALE" ? "#EFF6FF" : "#FDF2F8", color: selectedPatient.gender === "MALE" ? "#1D4ED8" : "#BE185D" }} />
                        <Chip label={`${selectedPatient.age} yrs`} size="small" sx={{ fontWeight: 600, bgcolor: "#F5F3FF", color: "#6D28D9" }} />
                        {selectedPatient.insurance && (
                          <Chip icon={<Shield sx={{ fontSize: 16 }} />} label={selectedPatient.insurance} size="small" sx={{ fontWeight: 600, bgcolor: "#FFF7ED", color: "#C2410C" }} />
                        )}
                      </Stack>
                    </Box>
                  </Box>
                  <Button variant="outlined" size="small" startIcon={<Search />} onClick={handleClearSelection}
                    sx={{ textTransform: "none", fontWeight: 600, borderColor: BORDER_COL, color: MUTED_COL, borderRadius: 2 }}>
                    New Search
                  </Button>
                </Box>
                <Grid container spacing={2}>
                  {[
                    { icon: <Phone sx={{ fontSize: 18, color: "#0D9488" }} />,       label: "Phone",         value: selectedPatient.tel || "—" },
                    { icon: <LocationOn sx={{ fontSize: 18, color: "#0D9488" }} />,  label: "Location",      value: selectedPatient.location || selectedPatient.res || "—" },
                    { icon: <CalendarToday sx={{ fontSize: 18, color: "#0D9488" }} />, label: "Date of Birth", value: selectedPatient.dob || "—" },
                    { icon: <LocalHospital sx={{ fontSize: 18, color: "#0D9488" }} />, label: "Status",       value: selectedPatient.state || "—" },
                  ].map((item, idx) => (
                    <Grid item xs={6} sm={3} key={idx}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: SOFT_BG, border: `1px solid ${BORDER_COL}` }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>{item.icon}
                          <Typography variant="caption" fontWeight={600} sx={{ color: MUTED_COL }}>{item.label}</Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={600} sx={{ color: TITLE_COL }}>{item.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Paper>

            {/* ════ VISIT HISTORY TABLE ════ */}
            <VisitHistoryTable patientData={selectedPatient} onOpenVisit={handleOpenVisit} />

            {/* ── Quick Access Cards ── */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: TITLE_COL, mb: 1 }}>View Patient History</Typography>
              <Typography variant="body2" sx={{ color: MUTED_COL }} sx={{ mb: 3 }}>Select a category below to view the patient's complete history</Typography>
            </Box>
            <Grid container spacing={2.5}>
              {historyCards.map((card) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={card.key}>
                  <Paper elevation={0} onClick={card.onClick}
                    sx={{
                      p: 3, borderRadius: 3, cursor: "pointer", border: `1px solid ${BORDER_COL}`,
                      transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)", position: "relative", overflow: "hidden",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: `0 12px 28px ${card.shadow}`, borderColor: "transparent",
                        "& .card-icon-box": { transform: "scale(1.1)" }, "& .card-arrow": { opacity: 1, transform: "translateX(0)" } },
                      "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: 3, background: card.gradient, borderRadius: "12px 12px 0 0" },
                    }}>
                    <Box className="card-icon-box" sx={{ width: 56, height: 56, borderRadius: 2.5, background: card.gradient, display: "flex", alignItems: "center", justifyContent: "center", color: "white", mb: 2, transition: "transform 0.25s ease", boxShadow: `0 4px 12px ${card.shadow}` }}>
                      {card.icon}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: TITLE_COL, mb: 0.5 }}>{card.title}</Typography>
                    <Typography variant="caption" sx={{ color: MUTED_COL, lineHeight: 1.4 }}>{card.subtitle}</Typography>
                    <Box className="card-arrow" sx={{ position: "absolute", bottom: 16, right: 16, opacity: 0, transform: "translateX(-8px)", transition: "all 0.25s ease", color: MUTED_COL, fontSize: "1.2rem" }}>→</Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* ── Empty state ── */}
        {!hasSearched && !selectedPatient && (
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e5e9ef", textAlign: "center", py: 5, px: 4, bgcolor: "#fff" }}>
            <Box sx={{ width: 56, height: 56, borderRadius: 1.75, bgcolor: "#F0FDFA", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.75 }}>
              <History sx={{ fontSize: 26, color: "#0D9488" }} />
            </Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: TITLE_COL, mb: 0.5 }}>Search for a Patient</Typography>
            <Typography sx={{ fontSize: "0.85rem", color: MUTED_COL, maxWidth: 380, mx: "auto" }}>
              Enter a patient ID or name in the search bar above to look up their complete medical history
            </Typography>
          </Paper>
        )}
      </Box>

      {/* ── Visit Record Dialog (new) ── */}
      <VisitRecordDialog
        open={visitRecordOpen}
        onClose={() => setVisitRecordOpen(false)}
        patientData={selectedPatient}
        visitRow={selectedVisitRow}
      />

      {/* ── Legacy dialogs ── */}
      <MedicalHistoryDialog  open={medHistoryOpen}    onClose={() => setMedHistoryOpen(false)}    patientData={selectedPatient} />
      <LabHistoryDialog      open={labHistoryOpen}    onClose={() => setLabHistoryOpen(false)}    patientData={selectedPatient} />
      <ScanHistoryDialog     open={scanHistoryOpen}   onClose={() => setScanHistoryOpen(false)}   patientData={selectedPatient} />
      <VitalsHistoryDialog   open={vitalsHistoryOpen} onClose={() => setVitalsHistoryOpen(false)} patientData={selectedPatient} />
      <MedicationHistory     open={medHisOpen}        onClose={() => setMedHisOpen(false)}        id={selectedPatient?.id || ""} />
    </Box>
  );
};

export default MedicalHistoryPage;